import { cn } from "@tcg/ui";
import { ShieldAlert, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { usePos } from "@/app/context";
import { CameraCapture } from "@/components/CameraCapture";
import { Modal } from "@/components/Modal";
import { call } from "@/lib/api";
import type {
  IdentityCheckCreate,
  IdentityCheckCreated,
  IdentityMethod,
} from "@/lib/consignment-types";
import { errorText, isApiError } from "@/lib/errors";
import { newIdempotencyKey } from "@/lib/idempotency";
import { uploadRestricted } from "@/lib/upload";

/** The 5 documents staff see at the counter (古物営業法施行規則15条 — 対面). */
export const DOCUMENTS: ReadonlyArray<{ method: IdentityMethod; key: string; number: boolean }> = [
  { method: "drivers_license", key: "drivers_license", number: true },
  // マイナンバー (個人番号) must not be written down — only that the card was seen
  { method: "my_number_card", key: "my_number_card", number: false },
  { method: "residence_card", key: "residence_card", number: true },
  { method: "passport", key: "passport", number: true },
  // 保険者番号・被保険者記号番号 may not be recorded (健康保険法 告知要求制限)
  { method: "health_insurance_card", key: "health_insurance_card", number: false },
];

/** Occupation choices for the 古物台帳 (stored in Japanese — the legal record). */
export const OCCUPATIONS = [
  "会社員",
  "自営業",
  "公務員",
  "パート・アルバイト",
  "学生",
  "主婦・主夫",
  "無職",
  "その他",
] as const;

export const ADULT_AGE = 18;

/** Age on `today` (JST date strings `YYYY-MM-DD`) — birthdays count from the day itself. */
export function ageOn(dateOfBirth: string, today: string): number | null {
  const birth = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOfBirth);
  const now = /^(\d{4})-(\d{2})-(\d{2})$/.exec(today);
  if (!birth || !now) {
    return null;
  }
  const [by, bm, bd] = birth.slice(1).map((v) => Number.parseInt(v, 10)) as [
    number,
    number,
    number,
  ];
  const [ny, nm, nd] = now.slice(1).map((v) => Number.parseInt(v, 10)) as [number, number, number];
  let age = ny - by;
  if (nm < bm || (nm === bm && nd < bd)) {
    age -= 1;
  }
  return age;
}

export function jstToday(now = new Date()): string {
  return new Date(now.getTime() + 9 * 3_600_000).toISOString().slice(0, 10);
}

const formSchema = z.object({
  fullName: z.string().trim().min(1),
  nameKana: z.string().trim().optional(),
  postalCode: z
    .string()
    .trim()
    .regex(/^\d{3}-?\d{4}$/),
  region: z.string().trim().min(1),
  city: z.string().trim().min(1),
  address1: z.string().trim().min(1),
  address2: z.string().trim().optional(),
  occupation: z.string().trim().min(1),
  dateOfBirth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  documentNumber: z.string().trim().max(64).optional(),
  documentExpiry: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .or(z.literal(""))
    .optional(),
});

type Form = z.input<typeof formSchema>;

const EMPTY: Form = {
  fullName: "",
  nameKana: "",
  postalCode: "",
  region: "",
  city: "",
  address1: "",
  address2: "",
  occupation: "",
  dateOfBirth: "",
  documentNumber: "",
  documentExpiry: "",
};

export interface IdentityCheckModalProps {
  open: boolean;
  onClose: () => void;
  onDone: (check: IdentityCheckCreated) => void;
  /** Who is identified: a customer (買取 with store credit) or a consignor (委託受付). */
  customerId?: string | null;
  consignorId?: string | null;
  /** 対価の総額 of the purchase / intake (the rule engine of consignment-service). */
  amount?: number;
  defaultName?: string | null;
}

/**
 * 本人確認 at the counter (T07-31, design/05 §3.4, 古物営業法15条): which document → a photo of it
 * (**uploaded straight to media-service's encrypted store** — the image never goes to pos-service)
 * → the few fields the 古物台帳 needs (氏名 · 住所 with 郵便番号 → 住所 · 職業 · 生年月日 · 番号) →
 * consignment-service records the check as verified. Under 18: the guardian's written consent must
 * be photographed too, or the purchase stops (APPI: nothing more than the law asks is kept).
 */
export function IdentityCheckModal({
  open,
  onClose,
  onDone,
  customerId,
  consignorId,
  amount = 0,
  defaultName,
}: IdentityCheckModalProps) {
  const { t } = useTranslation();
  const { consignment, media, orders } = usePos();
  const [doc, setDoc] = useState<(typeof DOCUMENTS)[number]>(
    DOCUMENTS[0] ?? {
      method: "drivers_license",
      key: "drivers_license",
      number: true,
    },
  );
  const [photo, setPhoto] = useState<Blob | null>(null);
  const [form, setForm] = useState<Form>({ ...EMPTY, fullName: defaultName ?? "" });
  const [minor, setMinor] = useState(false);
  const [guardian, setGuardian] = useState<Blob | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const set = (changes: Partial<Form>) => setForm((f) => ({ ...f, ...changes }));

  const parsed = formSchema.safeParse(form);
  const invalid = new Set(parsed.success ? [] : parsed.error.issues.map((i) => String(i.path[0])));
  const age = form.dateOfBirth ? ageOn(form.dateOfBirth, jstToday()) : null;
  const isMinor = age !== null && age < ADULT_AGE;

  const lookupPostal = async (code: string) => {
    const digits = code.replace(/\D/g, "");
    if (digits.length !== 7) {
      return;
    }
    try {
      const found = await call(() =>
        orders.GET("/v1/store/addresses/jp-postal/{postal_code}", {
          params: { path: { postal_code: digits } },
        }),
      );
      setForm((f) => ({
        ...f,
        region: found.prefecture,
        city: found.city,
        address1: f.address1 || found.town,
      }));
    } catch {
      // unknown code: the address is typed by hand (never blocking)
    }
  };

  const submit = async (withGuardian: Blob | null) => {
    setTouched(true);
    if (!parsed.success || !photo) {
      return;
    }
    if (isMinor && !withGuardian) {
      setMinor(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const idPhoto = await uploadRestricted(media, photo);
      const consent = withGuardian ? await uploadRestricted(media, withGuardian) : null;
      const data = parsed.data;
      const body: IdentityCheckCreate = {
        fullName: data.fullName,
        ...(data.nameKana ? { nameKana: data.nameKana } : {}),
        address: {
          country: "JP",
          postalCode: data.postalCode,
          region: data.region,
          city: data.city,
          address1: data.address1,
          ...(data.address2 ? { address2: data.address2 } : {}),
        },
        occupation: data.occupation,
        dateOfBirth: data.dateOfBirth,
        channel: "face_to_face",
        method: doc.method,
        documentType: t(`idcheck.doc_${doc.key}`, { lng: "ja" }),
        ...(doc.number && data.documentNumber ? { documentNumber: data.documentNumber } : {}),
        ...(data.documentExpiry ? { documentExpiry: data.documentExpiry } : {}),
        documentUploadIds: [idPhoto.assetId],
        ...(consent ? { guardianConsentDocId: consent.assetId } : {}),
        ...(customerId ? { customerId } : {}),
        ...(consignorId ? { consignorId } : {}),
        transactionTotal: { amount: String(amount), currencyCode: "JPY" },
      };
      const created = await call(() =>
        consignment.POST("/v1/pos/identity-checks", {
          body,
          params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        }),
      );
      setMinor(false);
      onDone(created);
    } catch (err) {
      if (isApiError(err, "blocked_minor")) {
        setMinor(true);
      } else {
        setError(errorText(err, t));
      }
    } finally {
      setBusy(false);
    }
  };

  const field = (name: keyof Form, label: string, extra: Record<string, unknown> = {}) => (
    <label className="flex flex-col gap-1">
      <span className="font-medium">{label}</span>
      <input
        value={form[name] ?? ""}
        onChange={(e) => set({ [name]: e.target.value } as Partial<Form>)}
        aria-invalid={touched && invalid.has(name) ? true : undefined}
        className={cn(
          "min-h-pos-touch rounded-md border px-3 text-lg",
          touched && invalid.has(name) && "border-status-problem",
        )}
        {...extra}
      />
    </label>
  );

  return (
    <>
      <Modal
        open={open && !minor}
        onClose={onClose}
        busy={busy}
        size="lg"
        title={t("idcheck.title")}
        description={t("idcheck.description")}
        footer={
          <>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="min-h-pos-touch rounded-md border px-6 text-lg"
            >
              {t("app.cancel")}
            </button>
            <button
              type="button"
              onClick={() => void submit(guardian)}
              disabled={busy}
              className="flex min-h-pos-touch items-center gap-2 rounded-md bg-primary px-8 text-lg font-semibold text-primary-foreground disabled:opacity-50"
            >
              <ShieldCheck aria-hidden className="size-6" />
              {busy ? t("app.saving") : t("idcheck.confirm")}
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <fieldset className="flex flex-wrap gap-2">
            <legend className="mb-1 font-medium">{t("idcheck.document")}</legend>
            {DOCUMENTS.map((d) => (
              <button
                key={d.key}
                type="button"
                aria-pressed={doc.key === d.key}
                onClick={() => setDoc(d)}
                className={cn(
                  "min-h-pos-touch rounded-md border px-4 text-lg",
                  doc.key === d.key && "border-primary bg-primary text-primary-foreground",
                )}
              >
                {t(`idcheck.doc_${d.key}`)}
              </button>
            ))}
          </fieldset>
          <CameraCapture label={t("idcheck.photo")} photo={photo} onPhoto={setPhoto} />
          {touched && !photo ? (
            <p role="alert" className="text-pos-problem">
              {t("idcheck.photo_required")}
            </p>
          ) : null}
          <p className="text-sm text-muted-foreground">{t("idcheck.photo_private")}</p>
          <div className="grid gap-3 md:grid-cols-2">
            {field("fullName", t("idcheck.full_name"), { autoComplete: "off" })}
            {field("nameKana", t("idcheck.name_kana"), { autoComplete: "off" })}
            {field("postalCode", t("idcheck.postal_code"), {
              inputMode: "numeric",
              placeholder: "100-0001",
              onBlur: (e: React.FocusEvent<HTMLInputElement>) => void lookupPostal(e.target.value),
            })}
            {field("region", t("idcheck.region"))}
            {field("city", t("idcheck.city"))}
            {field("address1", t("idcheck.address1"))}
            {field("address2", t("idcheck.address2"))}
            <label className="flex flex-col gap-1">
              <span className="font-medium">{t("idcheck.occupation")}</span>
              <select
                value={form.occupation}
                onChange={(e) => set({ occupation: e.target.value })}
                aria-invalid={touched && invalid.has("occupation") ? true : undefined}
                className={cn(
                  "min-h-pos-touch rounded-md border px-3 text-lg",
                  touched && invalid.has("occupation") && "border-status-problem",
                )}
              >
                <option value="">{t("idcheck.choose")}</option>
                {OCCUPATIONS.map((o) => (
                  <option key={o} value={o}>
                    {t(`idcheck.occupation_${String(OCCUPATIONS.indexOf(o))}`)}
                  </option>
                ))}
              </select>
            </label>
            {field("dateOfBirth", t("idcheck.date_of_birth"), { type: "date" })}
            {doc.number ? (
              field("documentNumber", t("idcheck.document_number"), { autoComplete: "off" })
            ) : (
              <p className="self-end rounded-md bg-muted p-3 text-sm">
                {t(`idcheck.no_number_${doc.key}`)}
              </p>
            )}
            {field("documentExpiry", t("idcheck.document_expiry"), { type: "date" })}
          </div>
          {age !== null ? (
            <p className={cn("text-lg", isMinor && "font-semibold text-pos-problem")}>
              {t("idcheck.age", { age })}
            </p>
          ) : null}
          {touched && !parsed.success ? (
            <p role="alert" className="text-pos-problem">
              {t("idcheck.fill_required")}
            </p>
          ) : null}
          {error ? (
            <p role="alert" className="text-pos-problem">
              {error}
            </p>
          ) : null}
        </div>
      </Modal>
      <Modal
        open={open && minor}
        onClose={() => setMinor(false)}
        busy={busy}
        title={t("idcheck.minor_title")}
        description={t("idcheck.minor_body")}
        footer={
          <>
            <button
              type="button"
              onClick={() => {
                setMinor(false);
                setGuardian(null);
                onClose();
              }}
              disabled={busy}
              className="min-h-pos-touch rounded-md border px-6 text-lg"
            >
              {t("idcheck.minor_stop")}
            </button>
            <button
              type="button"
              onClick={() => void submit(guardian)}
              disabled={busy || !guardian}
              className="min-h-pos-touch rounded-md bg-primary px-6 text-lg font-semibold text-primary-foreground disabled:opacity-50"
            >
              {t("idcheck.minor_continue")}
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="flex items-center gap-2 text-lg text-pos-problem">
            <ShieldAlert aria-hidden className="size-6" />
            {t("idcheck.age", { age: age ?? "—" })}
          </p>
          <CameraCapture
            label={t("idcheck.guardian_photo")}
            guide="document"
            photo={guardian}
            onPhoto={setGuardian}
          />
          {!guardian ? (
            <p className="text-muted-foreground">{t("idcheck.minor_needs_doc")}</p>
          ) : null}
        </div>
      </Modal>
    </>
  );
}
